import { withItem, withoutItem } from 'src/network/cache/listItems';

import { RoomHighlights } from './RoomHighlights.query';
import { RoomNotes } from './RoomNotes.query';

import type { RoomHighlight } from './RoomHighlights.query';
import type { RoomNote } from './RoomNotes.query';
import type { Cache } from '@urql/exchange-graphcache';

const stringArgument = (value: unknown) => (typeof value === 'string' ? value : '');

const cachedArguments = (cache: Cache, fieldName: string) =>
  cache
    .inspectFields('Query')
    .flatMap(field => (field.fieldName === fieldName && field.arguments ? [field.arguments] : []));

/** A cached list takes a new item once, as the mutation result and its own event can both add it. */
export const addHighlightToCache = (cache: Cache, meetingCode: string, highlight: RoomHighlight) =>
  cache.updateQuery({ query: RoomHighlights, variables: { meetingCode, roomId: highlight._room_id } }, data =>
    data ? { playbackGetHighlights: withItem(data.playbackGetHighlights, highlight) } : null,
  );

export const removeHighlightFromCache = (cache: Cache, id: string) =>
  cachedArguments(cache, 'playbackGetHighlights').forEach(args =>
    cache.updateQuery(
      {
        query: RoomHighlights,
        variables: { meetingCode: stringArgument(args.meeting_code), roomId: stringArgument(args._room_id) },
      },
      data => (data ? { playbackGetHighlights: withoutItem(data.playbackGetHighlights, id) } : null),
    ),
  );

export const addNoteToCache = (cache: Cache, { code, roomId }: { code: string; roomId: string }, note: RoomNote) =>
  cache.updateQuery({ query: RoomNotes, variables: { code, _room_id: roomId } }, data =>
    data ? { roomNotes: withItem(data.roomNotes, note) } : null,
  );

/** A note event names no room, so the note leaves every cached room of the meeting. */
export const removeNoteFromCache = (cache: Cache, code: string, id: string) =>
  cachedArguments(cache, 'roomNotes')
    .filter(args => args.code === code)
    .forEach(args =>
      cache.updateQuery({ query: RoomNotes, variables: { code, _room_id: stringArgument(args._room_id) } }, data =>
        data ? { roomNotes: withoutItem(data.roomNotes, id) } : null,
      ),
    );
